/**
 * UI Components Index
 * Barrel export for all UI components.
 * @packageDocumentation
 */

import { memo } from 'react';
import type { JSX, ReactNode } from 'react';
import { X, Search, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PipelineNetworkLogo } from '@/components/icons';

import { Skeleton } from './skeleton';
import { Card, CardContent } from './card';

export { Label } from './label';
export { Toaster } from './sonner';
export { Skeleton } from './skeleton';
export { UserMenu } from './UserMenu';
export { Separator } from './separator';
export { ErrorBoundary } from './ErrorBoundary';
export { CardContent as CardBody } from './card';
export {
  Badge,
  type BadgeProps,
} from './badge';
export {
  Input,
  type InputProps,
} from './input';
export {
  Textarea,
  type TextareaProps,
} from './textarea';
export {
  HelpTooltip,
  RuleTypeTooltip,
} from './HelpPanel';
export {
  FormField,
  type FormFieldProps,
} from './form-field';
export { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog';
export {
  Pagination,
  type PaginationProps,
} from './Pagination';
export {
  Button,
  type ButtonProps,
  buttonVariants,
} from './button';
export {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './tabs';
export {
  ConfirmDialog,
  type ConfirmDialogProps,
} from './ConfirmDialog';
export {
  SectionHeading,
  type SectionHeadingProps,
} from './SectionHeading';
export {
  Breadcrumb,
  type BreadcrumbItem,
  type BreadcrumbProps,
} from './breadcrumb';
export {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from './popover';
export {
  CollapsibleSection,
  type CollapsibleSectionProps,
} from './collapsible-section';
export {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
export {
  ChangeHistoryDialog,
  type ChangeHistoryDialogProps,
} from './ChangeHistoryDialog';
export {
  EnvironmentSelector,
  type EnvironmentSelectorProps,
} from './EnvironmentSelector';
export {
  EnvironmentBadge,
  ENV_BADGE_COLORS,
  type EnvironmentBadgeProps,
} from './EnvironmentBadge';
export {
  TrafficLightMenu,
  type TrafficLightMenuItem,
  type TrafficLightMenuProps,
} from './TrafficLightMenu';
export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  CardToolbar,
} from './card';
export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from './select';
export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from './dialog';

/**
 * Props for the SearchInput component.
 */
export interface SearchInputProps {
  /** The current search value. */
  value: string;
  /** Additional CSS classes for the wrapper. */
  className?: string;
  /** The input placeholder text. */
  placeholder: string;
  /** Callback fired when the value changes. */
  onChange: (value: string) => void;
}

/**
 * Search input with magnifying glass icon and optional clear button.
 *
 * @param { SearchInputProps } props Component props.
 * @returns { JSX.Element } The search input element.
 */
export const SearchInput = ({ value, onChange, className, placeholder }: SearchInputProps): JSX.Element => (
  <div className={cn('relative flex items-center', className)}>
    <Search size={20} className="pointer-events-none absolute left-3 text-muted-foreground" />
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-border bg-muted/30 py-2 pl-10 pr-9 text-sm text-foreground outline-none transition-colors focus:border-primary"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-2.5 rounded-md p-0.5 text-muted-foreground transition-colors hover:text-foreground"
      >
        <X size={16} />
      </button>
    )}
  </div>
);

/**
 * Props for the FilterCheckbox component.
 */
export interface FilterCheckboxProps {
  /** The checkbox label text. */
  label: string;
  /** Whether the checkbox is checked. */
  checked: boolean;
  /** Additional CSS classes. */
  className?: string;
  /** Callback fired when the checked state changes. */
  onChange: (checked: boolean) => void;
}

/**
 * Checkbox with label for toolbar filter toggles.
 *
 * @param { FilterCheckboxProps } props Component props.
 * @returns { JSX.Element } The filter checkbox element.
 */
export const FilterCheckbox = ({ label, checked, onChange, className }: FilterCheckboxProps): JSX.Element => (
  <label
    className={cn('flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-muted-foreground', className)}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="size-4 cursor-pointer accent-primary"
    />{' '}
    {label}
  </label>
);

/**
 * Status type for StatCard styling.
 */
export type StatCardStatus = 'default' | 'info' | 'success' | 'warning' | 'error';

/**
 * Props for the StatCard component.
 */
export interface StatCardProps {
  /** The stat title. */
  title: string;
  /** Optional icon element. */
  icon?: ReactNode;
  /** The stat value. */
  value: ReactNode;
  /** Secondary text displayed below the value for ratio context. */
  subtitle?: string;
  /** Additional CSS classes. */
  className?: string;
  /** Whether the card has hover/interactive styling. Defaults to true. Set false for display-only cards. */
  interactive?: boolean;
  /** Status determines icon background color (default: Google blue, success: green, warning: yellow, error: red). */
  status?: StatCardStatus;
  /** Optional trend indicator. */
  trend?: {
    /** The trend percentage value. */
    value: number;
    /** Whether the trend is positive. */
    isPositive: boolean;
    /** Comparison context label (e.g., "vs. yesterday", "last 24h: 12,400"). */
    comparisonLabel?: string;
  };
}

const statCardIconStyles: Record<StatCardStatus, string> = {
  info: 'bg-info/5 text-info',
  success: 'bg-success/6 text-success',
  warning: 'bg-warning/6 text-warning',
  default: 'bg-muted text-muted-foreground',
  error: 'bg-destructive/6 text-destructive',
};

/**
 * Statistics card component for displaying metrics.
 *
 * @param { StatCardProps } props Component props.
 * @returns { JSX.Element } The stat card element.
 */
const StatCardInner = ({
  icon,
  title,
  value,
  trend,
  subtitle,
  className,
  interactive = true,
  status = 'default',
}: StatCardProps): JSX.Element => (
  <Card className={cn('h-full', interactive && 'card-interactive', className)}>
    <CardContent className="flex h-full items-center px-6 py-5">
      <div className="flex items-center gap-3">
        {icon && (
          <div
            className={cn('flex size-9 shrink-0 items-center justify-center rounded-md', statCardIconStyles[status])}
          >
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-muted-foreground">{title}</p>
          <p className="animate-count-up text-2xl font-medium leading-tight tabular-nums tracking-tight text-foreground">
            {value}
          </p>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
          {trend && (
            <p className={cn('mt-0.5 text-xs', trend.isPositive ? 'text-success' : 'text-destructive')}>
              {trend.isPositive ? '\u2191' : '\u2193'} {Math.abs(trend.value)}%
              {trend.comparisonLabel && <span className="ml-1 text-muted-foreground">{trend.comparisonLabel}</span>}
            </p>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const StatCard = memo(StatCardInner);

/** Empty state variant type. */
type EmptyStateVariant = 'default' | 'featured';

/**
 * Props for the EmptyState component.
 */
export interface EmptyStateProps {
  /** The empty state title. */
  title: string;
  /** Optional icon element. */
  icon?: ReactNode;
  /** Optional action element. */
  action?: ReactNode;
  /** Optional description text. */
  description?: string;
  /** Visual variant: default (simple) or featured (with circular icon background). */
  variant?: EmptyStateVariant;
}

/**
 * Empty state component for displaying when no data is available.
 *
 * @param { EmptyStateProps } props Component props.
 * @returns { JSX.Element } The empty state element.
 */
export const EmptyState = ({ icon, title, action, description, variant = 'default' }: EmptyStateProps): JSX.Element => (
  <div className={cn('animate-scale-in text-center', variant === 'featured' ? 'py-20' : 'py-12')}>
    {icon &&
      (variant === 'featured' ? (
        <div className="mb-6 flex justify-center">
          <div className="flex size-20 items-center justify-center rounded-full bg-accent text-accent-foreground ring-4 ring-accent/30 ring-offset-2 ring-offset-background">
            {icon}
          </div>
        </div>
      ) : (
        <div className="mb-4 flex justify-center text-muted-foreground">{icon}</div>
      ))}
    <h3
      className={cn(
        'text-foreground',
        variant === 'featured' ? 'mb-3 text-xl font-normal' : 'mb-1 text-lg font-medium',
      )}
    >
      {title}
    </h3>
    {description && <p className="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground">{description}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

/**
 * Props for the LoadingSpinner component.
 */
export interface LoadingSpinnerProps {
  /** Additional CSS classes. */
  className?: string;
  /** The spinner size. */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Loading spinner component with size variants.
 *
 * @param { LoadingSpinnerProps } props Component props.
 * @returns { JSX.Element } The loading spinner element.
 */
export const LoadingSpinner = ({ className, size = 'md' }: LoadingSpinnerProps) => {
  const sizes = {
    sm: 'size-4',
    md: 'size-6',
    lg: 'size-8',
  };

  return <Loader2 className={cn('animate-spin text-primary', sizes[size], className)} />;
};

/**
 * Props for the AppLoader component.
 */
export interface AppLoaderProps {
  /** When false, fills the parent container instead of the full viewport. Defaults to true. */
  fullScreen?: boolean;
}

/**
 * App loader with spinning ring and logo inside.
 * Matches the Hostinger-style large spinner with icon center.
 *
 * @param { AppLoaderProps } props Component props.
 * @returns { JSX.Element } The app loader element.
 */
export const AppLoader = ({ fullScreen = true }: AppLoaderProps): JSX.Element => (
  <div
    className={cn(
      'flex items-center justify-center bg-background',
      fullScreen ? 'min-h-screen' : 'h-[calc(100vh-4rem)]',
    )}
  >
    <div className="relative flex items-center justify-center" style={{ width: 120, height: 120 }}>
      {/* Spinning arc ring */}
      <svg
        className="absolute inset-0 animate-spin"
        style={{ animationDuration: '1.4s', animationTimingFunction: 'linear' }}
        width={120}
        height={120}
        viewBox="0 0 120 120"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="60" cy="60" r="54" className="stroke-border" strokeWidth="3" />
        <path d="M 60 6 A 54 54 0 0 1 114 60" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
      </svg>
      {/* Logo centered inside */}
      <PipelineNetworkLogo size={40} className="text-primary" color="currentColor" />
    </div>
  </div>
);

/**
 * Table column configuration.
 */
export interface TableColumn<T> {
  /** The column key matching the data property. */
  key: string;
  /** The column header text. */
  header: string;
  /** Additional CSS classes for the column. */
  className?: string;
  /** ReactNode header, takes precedence over `header` string when provided. */
  headerNode?: ReactNode;
  /** Custom render function for the cell. */
  render?: (item: T) => ReactNode;
}

/**
 * Props for the DataTable component.
 */
interface DataTableProps<T> {
  /** Array of data items. */
  data: T[];
  /** Whether the table is loading. */
  isLoading?: boolean;
  /** Extra classes applied to every `<th>` cell (e.g. to override padding). */
  thClassName?: string;
  /** When true, suppresses rendering of the <thead> (use when headers are rendered externally). */
  hideHeader?: boolean;
  /** Custom empty state element. */
  emptyState?: ReactNode;
  /** Makes the thead sticky within a scrolling container. */
  stickyHeader?: boolean;
  /** Extra classes applied to the `<thead>` element. */
  theadClassName?: string;
  /** Extra classes applied to the `<table>` element. */
  tableClassName?: string;
  /** Optional <colgroup> element to inject for fixed column widths. */
  colGroupSlot?: ReactNode;
  /** Array of column configurations. */
  columns: TableColumn<T>[];
  /** CSS `top` offset for the sticky thead (pixels or string like 'calc(...)'). */
  stickyTop?: number | string;
  /** Callback fired when a row is clicked. */
  onRowClick?: (item: T) => void;
  /** Function to extract unique key from each item. */
  keyExtractor: (item: T) => string;
  /** Per-row dynamic class callback. When provided, suppresses default hover. */
  getRowClassName?: (item: T) => string;
}

/**
 * Generic data table component with loading and empty states.
 *
 * @param { DataTableProps<T> } props Component props.
 * @returns { JSX.Element } The data table element.
 */
export const DataTable = <T,>({
  data,
  columns,
  isLoading,
  stickyTop,
  onRowClick,
  emptyState,
  hideHeader,
  thClassName,
  keyExtractor,
  stickyHeader,
  colGroupSlot,
  tableClassName,
  theadClassName,
  getRowClassName,
}: DataTableProps<T>) => {
  const theadStyle =
    stickyHeader && stickyTop !== undefined
      ? { top: typeof stickyTop === 'number' ? `${stickyTop}px` : stickyTop }
      : undefined;

  if (isLoading) {
    return (
      <table className={cn('w-full border-separate border-spacing-0 text-sm', tableClassName)}>
        {colGroupSlot}
        {!hideHeader && (
          <thead className={cn(!stickyHeader && 'bg-muted/50', !stickyHeader && theadClassName)}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'border-b border-border px-6 py-3 text-left text-xs font-medium text-foreground/60',
                    stickyHeader && 'sticky z-10 bg-card transition-shadow duration-200',
                    stickyHeader && theadClassName,
                    thClassName,
                  )}
                  style={theadStyle}
                >
                  <Skeleton className="h-4 w-20" />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4', 'skeleton-5'].map((rowId) => (
            <tr key={rowId} className="transition-colors">
              {columns.map((col) => (
                <td key={col.key} className="border-b border-border px-6 py-3">
                  <Skeleton className="h-4 w-full" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <table className={cn('w-full border-separate border-spacing-0 text-sm', tableClassName)}>
      {colGroupSlot}
      {!hideHeader && (
        <thead className={cn(!stickyHeader && 'bg-muted/50', !stickyHeader && theadClassName)}>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'border-b border-border px-6 py-3 text-left text-xs font-medium text-foreground/60',
                  stickyHeader && 'sticky z-10 bg-card transition-shadow duration-200',
                  stickyHeader && theadClassName,
                  thClassName,
                  col.className,
                )}
                style={theadStyle}
              >
                {col.headerNode ?? col.header}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {data.map((item) => (
          <tr
            key={keyExtractor(item)}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
            className={cn(
              'transition-colors',
              onRowClick && 'cursor-pointer',
              getRowClassName ? getRowClassName(item) : 'hover:bg-muted/15',
            )}
          >
            {columns.map((col) => (
              <td key={col.key} className={cn('border-b border-border px-6 py-3', col.className)}>
                {col.render ? col.render(item) : `${item[col.key as keyof T] ?? ''}`}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './dropdown-menu';
